/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Building2, Coins, FileSignature, Users, ScrollText, Stamp, AlertCircle, Key,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/acte-de-vente',
  slug: 'acte-de-vente',
  breadcrumbLabel: 'Acte de vente',
  readingTime: '8 min',
  lastUpdated: '18 février 2026',
  seoTitle: 'Acte de vente immobilière : guide simple | HÉRITA',
  seoDescription:
    "Comprenez facilement un acte de vente immobilier : prix, conditions, servitudes, frais, obligations. Analysez votre acte authentique grâce à HÉRITA.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre un acte de vente immobilier',
    subtitle:
      "L'acte de vente est le document qui officialise le transfert d'un bien immobilier entre un vendeur et un acheteur. HÉRITA vous aide à comprendre le prix, les conditions, les frais et toutes les obligations qui en découlent.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'un acte de vente immobilier\u00A0?",
    paragraphs: [
      <>
        L'acte de vente immobilier — aussi appelé{' '}
        <em>acte authentique de vente</em> — est le document signé chez le
        notaire qui scelle, juridiquement et fiscalement,{' '}
        <strong className="font-semibold text-[#1A2530]">le transfert de propriété</strong>{' '}
        d'un bien immobilier entre un vendeur et un acheteur. C'est lui qui
        rend la vente définitive.
      </>,
      <>
        Il intervient en fin de processus. En amont, il a généralement été
        précédé d'un <em>compromis de vente</em> ou d'une <em>promesse de
        vente</em>, signés quelques mois plus tôt, qui fixaient déjà le prix
        et les conditions essentielles. Entre les deux, le notaire mène ses
        vérifications (titres, urbanisme, hypothèques, droit de préemption).
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Pourquoi un notaire&nbsp;?</strong>{' '}
        Parce qu'une vente immobilière ne peut être opposée à tous qu'à
        condition d'être publiée au service de la publicité foncière. Seul un
        acte authentique notarié peut être ainsi publié. Le notaire authentifie,
        prélève les taxes, et transmet l'acte à l'administration.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Que contient-il&nbsp;?</strong>{' '}
        L'identité du vendeur et de l'acheteur, la description précise du
        bien, le prix de vente, les modalités de paiement, les diagnostics
        techniques obligatoires, l'origine de propriété sur trente ans, les
        servitudes éventuelles, les charges de copropriété, les conditions
        suspensives qui ont été levées, et les frais.
      </>,
      <>
        Les fameux <em>frais de notaire</em> ne reviennent d'ailleurs qu'en
        très faible partie au notaire lui-même. Ils correspondent
        principalement à des droits de mutation reversés au Trésor public et
        aux collectivités locales (environ 7 à 8 % du prix dans l'ancien, 2
        à 3 % dans le neuf).
      </>,
      <>
        Comprendre un acte de vente, c'est comprendre exactement ce que vous
        achetez ou vendez. HÉRITA met en avant les chiffres clés, les
        servitudes, les conditions particulières et fait le lien avec
        l'éventuelle{' '}
        <Link to="/attestation-immobiliere" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">attestation immobilière</Link>
        {' '}qui aurait précédé la vente dans le cadre d'une succession.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert-il\u00A0?",
    items: [
      { title: 'Transférer la propriété', description: "L'acte authentique fait de l'acheteur le nouveau propriétaire du bien à compter du jour de la signature." },
      { title: 'Rendre la vente opposable', description: "La publication au service de la publicité foncière rend le transfert opposable à tous : voisins, banques, État." },
      { title: 'Fixer les obligations', description: "Toutes les obligations du vendeur (garantie des vices cachés, conformité) et de l'acheteur (paiement) y sont consignées." },
      { title: 'Préserver vos droits', description: "L'acte authentique a force exécutoire : il vaut titre devant tous les tribunaux, sans procédure préalable." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient un acte de vente\u00A0?",
    intro: "Huit éléments structurent l'acte. Leur lecture permet de comprendre ce qui est vendu, à quel prix, et dans quelles conditions.",
    cards: [
      { Icon: Users, title: 'Vendeur et acheteur', description: "Identité complète des parties, état civil, situation matrimoniale et capacité juridique de chacun." },
      { Icon: Building2, title: 'Description du bien', description: "Adresse exacte, nature (maison, appartement, terrain), surface, lots de copropriété, dépendances." },
      { Icon: Coins, title: 'Prix de vente', description: "Montant net vendeur, modalités de paiement, comptes séquestres, échéances et indexations éventuelles." },
      { Icon: FileSignature, title: 'Conditions suspensives', description: "Obtention de prêt, accord d'urbanisme, levée du droit de préemption : chaque condition est listée et datée." },
      { Icon: ScrollText, title: 'Origine de propriété', description: "Historique du bien sur 30 ans minimum, vente après vente, donation après donation." },
      { Icon: AlertCircle, title: 'Servitudes', description: "Droits de passage, vues, mitoyenneté, restrictions d'urbanisme qui grèvent le bien." },
      { Icon: Stamp, title: 'Diagnostics', description: "Performance énergétique, plomb, amiante, termites, gaz, électricité, ERP : tous joints à l'acte." },
      { Icon: Key, title: 'Frais et taxes', description: "Détail des droits de mutation, émoluments du notaire, frais d'enregistrement et taxe de publicité foncière." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "Un acte de vente vous engage à long terme. Voici ce qu'il faut absolument relire avant de signer.",
    items: [
      "Vérifier que le prix de vente et les modalités de paiement correspondent à ce qui a été convenu au compromis.",
      "S'assurer que toutes les conditions suspensives ont bien été levées avant la signature.",
      "Identifier toutes les servitudes : un droit de passage ou une vue peut limiter l'usage du bien.",
      "Lire attentivement les diagnostics obligatoires, notamment le DPE et l'amiante.",
      "Vérifier l'absence d'inscription d'hypothèque non levée sur le bien.",
      "Confirmer le détail des frais : ils doivent correspondre au devis estimatif fourni en amont.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import de l'acte, HÉRITA en extrait les éléments essentiels.",
    items: [
      'Vendeur et acheteur identifiés',
      'Bien et ses caractéristiques',
      'Prix de vente et modalités',
      'Conditions suspensives levées',
      'Servitudes et restrictions',
      'Diagnostics techniques',
      'Frais et taxes détaillés',
      "Démarches d'enregistrement",
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif d'acte de vente. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;Monsieur et Madame MARTIN vendent à Mademoiselle Pauline GIRARD
        l'appartement sis 8 avenue Victor Hugo à Paris 16ᵉ (75016), lot
        n°&nbsp;12, d'une surface Carrez de 64 m², au prix net vendeur de
        SEPT CENT CINQUANTE MILLE EUROS (750 000 €), payé comptant à la
        signature. Le bien est vendu libre de toute occupation, après levée
        de la condition suspensive d'obtention du prêt d'un montant maximum
        de 600 000 €.&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour Pauline",
    bullets: [
      { strong: 'Vous devenez propriétaire', rest: " de l'appartement, lot 12, 64 m² Carrez, au 8 avenue Victor Hugo à Paris 16ᵉ." },
      { strong: 'Prix payé', rest: ' : 750 000 € net vendeur, en totalité à la signature.' },
      { strong: 'Votre prêt de 600 000 €', rest: ' a bien été obtenu — la condition suspensive est levée.' },
      { strong: 'Le bien est libre', rest: ' de toute occupation : vous pouvez en disposer immédiatement.' },
      { strong: 'Frais à prévoir', rest: " : environ 56 000 € (7,5 % du prix) pour les droits de mutation et les émoluments du notaire." },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'un acte de vente ?", a: "C'est un acte authentique signé chez le notaire qui transfère officiellement la propriété d'un bien immobilier du vendeur à l'acheteur. Sa publication au service de la publicité foncière rend la vente opposable à tous." },
    { q: "Quelle différence avec un compromis ?", a: "Le compromis (ou promesse de vente) engage les parties à conclure la vente sous certaines conditions. L'acte authentique, lui, finalise la vente. Entre les deux s'écoulent en général 2 à 3 mois durant lesquels le notaire effectue ses vérifications." },
    { q: "Que paient exactement les frais de notaire ?", a: "Ils se composent principalement des droits de mutation (taxes reversées à l'État et aux collectivités), des frais d'enregistrement, et d'une rémunération encadrée du notaire (les émoluments). Comptez 7 à 8 % dans l'ancien et 2 à 3 % dans le neuf." },
    { q: "Quand devient-on propriétaire ?", a: "Dès la signature de l'acte authentique. La propriété passe au moment précis de la signature et de la remise des clés. La publication au service de la publicité foncière intervient ensuite, dans les semaines qui suivent." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA identifie le bien, le prix, les conditions suspensives, les servitudes, les diagnostics, le détail des frais et les obligations de chaque partie. Vous obtenez une lecture claire en quelques minutes, vraiment utile avant ou après signature." },
    { q: "Peut-on revenir en arrière ?", a: "Pas après la signature de l'acte authentique. Avant, l'acheteur dispose d'un délai de rétractation de 10 jours suivant la notification du compromis. Une fois l'acte signé, seul le vice du consentement (dol, erreur, violence) peut justifier une annulation." },
    { q: "Qui choisit le notaire ?", a: "Vendeur et acheteur peuvent chacun avoir leur notaire, sans surcoût : les deux se partagent les émoluments. À défaut, un seul notaire signe pour les deux parties, comme c'est encore souvent le cas en pratique." },
    { q: "Qu'est-ce qu'une servitude ?", a: "Une servitude est un droit attaché à un bien immobilier qui limite l'usage que peut en faire son propriétaire (droit de passage du voisin, servitude de vue, mitoyenneté). Toute servitude doit être mentionnée dans l'acte de vente." },
    { q: "Qu'est-ce que la garantie des vices cachés ?", a: "Le vendeur garantit l'acheteur contre les vices non apparents qui rendraient le bien impropre à son usage. L'acheteur dispose de 2 ans après leur découverte pour agir. Une clause peut limiter cette garantie entre particuliers." },
    { q: "Quel rapport avec une succession ?", a: "Si vous vendez un bien hérité, l'acte de vente fera référence à l'attestation immobilière qui a constaté votre devenir-propriétaire. La plus-value éventuelle se calcule à partir de la valeur retenue dans cette attestation." },
  ],

  finalCta: {
    title: "Vous avez reçu un acte de vente\u00A0?",
    subtitle: "Déposez votre document. HÉRITA identifie automatiquement le bien, le prix, les conditions, les servitudes, les diagnostics et les frais afin de vous fournir une lecture claire et personnalisée avant ou après signature.",
  },
};

const ActeDeVente = () => <GuidePage config={config} />;
export default ActeDeVente;
